import {
  Box,
  IconButton,
  Icons,
  Modal,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import React from 'react';
import { createStyles } from './AssignmentMaterials.styles';
import pushRoute from '@app/utils/routing';
import { getCurrentSpaceId } from '@core/selectors/space';
import { useSelector } from 'react-redux';
import { AulaState } from '@core/types/state';
import useIsClamped from '@app/hooks/useIsClamped';

interface Material {
  id: string;
  title: string;
}

type Props = {
  open: boolean;
  onClose: () => void;
  linkedMaterials: Array<Material>;
};

type TextProps = {
  text: string;
  isDesktop: boolean;
  id?: string;
  wrapperSx?: Record<string, unknown>;
  textSx: Record<string, unknown>;
  clampLines?: number;
};

const AssignmentMaterials = ({ open, onClose, linkedMaterials }: Props) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const state = useSelector((state: AulaState) => state);
  const spaceId = getCurrentSpaceId(state as AulaState);

  const goToPage = (materialId: string) => {
    const urlPath = `/dashboard/${spaceId}/journey/materials/${materialId}?from=assignments`;
    pushRoute(urlPath);
  };

  const title = `Materials page linked (${linkedMaterials.length} pages)`;
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="assignment-materials"
      aria-modal="true"
    >
      <Box sx={styles.modalBody} role="dialog">
        <Box sx={styles.modalHeader}>
          <Typography
            id="assignment-materials-modal-title"
            sx={styles.modalTitle}
          >
            <OverflowText
              text={title}
              isDesktop={isDesktop}
              wrapperSx={styles.modalTitleWrapper}
              textSx={styles.modalTitle}
              clampLines={1}
            />
          </Typography>
          <IconButton
            aria-label="Close extensions modal"
            onClick={onClose}
            sx={styles.modalClose}
          >
            <Tooltip
              title="Close"
              disableHoverListener={!isDesktop}
              disableFocusListener={!isDesktop}
              disableTouchListener
            >
              <Icons.Close />
            </Tooltip>
          </IconButton>
        </Box>
        <Box sx={styles.modalContent}>
          {linkedMaterials.map((linkedMaterial) => {
            return (
              <Box key={linkedMaterial.id} sx={styles.materialRow}>
                <Box sx={styles.materialIcon}>
                  <Icons.FeedOutlined />
                </Box>
                <Box sx={styles.materialDetails}>
                  <Box sx={{ flex: '1' }}>
                    <OverflowText
                      text={linkedMaterial.title}
                      isDesktop={isDesktop}
                      wrapperSx={styles.titleWrapper}
                      textSx={styles.title}
                    />
                    <Box sx={styles.lastEdited}>Last edited: Today 12:30</Box>
                  </Box>
                  <Box
                    onClick={() => goToPage(linkedMaterial.id)}
                    sx={styles.goToPageButton}
                  >
                    Go to the page
                    <Icons.ArrowForwardIos sx={{ fontSize: '14px' }} />
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Modal>
  );
};

export default AssignmentMaterials;

const OverflowText = ({
  text,
  isDesktop,
  id,
  wrapperSx,
  textSx,
  clampLines = 1,
}: TextProps) => {
  const [textRef, isTextClamped] = useIsClamped<HTMLDivElement>(text);
  const isDisabled = !isDesktop || !isTextClamped;

  return (
    <Tooltip
      disableInteractive
      title={text}
      disableFocusListener={isDisabled}
      disableHoverListener={isDisabled}
      disableTouchListener={isDisabled}
    >
      <Box sx={{ ...(wrapperSx ?? {}) }}>
        <Box
          id={id}
          ref={textRef}
          sx={{ ...textSx, WebkitLineClamp: clampLines }}
        >
          {text}
        </Box>
      </Box>
    </Tooltip>
  );
};
